import { useRuntimeEngineSections } from '@framework/engine/schema/sections/hooks/useRuntimeEngineSections.tsx';
import {
  type IEngineRuntimeSection,
  type IEngineSection,
} from '@framework/engine/schema/sections/IEngineSection.ts';
import { useEffect, useRef } from 'react';
import { useModuleFormSectionsMonitorContext } from './ModuleFormSectionsMonitorContext.tsx';

export interface IModuleFormSectionsMonitorProps {
  sections: IEngineSection[];
}

/**
 * Confronta i risultati runtime (visible/enabled) di due snapshot di sezioni.
 * Ritorna `true` se i booleani sono identici per tutte le sezioni.
 */
function areSameRuntimeResults(
  prev: IEngineRuntimeSection[],
  next: IEngineRuntimeSection[],
): boolean {
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    if (
      prev[i].visible !== next[i].visible ||
      prev[i].enabled !== next[i].enabled
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Sincronizza la visibilità delle sezioni con i valori del form.
 */
export function ModuleFormSectionsMonitor(
  props: IModuleFormSectionsMonitorProps,
) {
  const { sections } = props;

  const computedRuntimeSections = useRuntimeEngineSections(sections);
  const { setRuntimeSections } = useModuleFormSectionsMonitorContext();
  const prevRef = useRef<IEngineRuntimeSection[] | null>(null);

  // Aggiorniamo il context solo quando i booleani visible/enabled cambiano
  // effettivamente, evitando di propagare re-render a tutti i FillableForm
  // quando useBooleanConditions ritorna un nuovo array con gli stessi risultati.
  // biome-ignore lint/correctness/useExhaustiveDependencies: confronto manuale con prevRef
  useEffect(() => {
    if (
      prevRef.current === null ||
      !areSameRuntimeResults(prevRef.current, computedRuntimeSections)
    ) {
      prevRef.current = computedRuntimeSections;
      setRuntimeSections(computedRuntimeSections);
    }
  }, [computedRuntimeSections]);

  return null;
}
